import React from 'react';
import {Skeleton} from '@ui/skeleton/skeleton';
import {Trans} from '@ui/i18n/trans';
import {UserThemeInfo} from '@ui/themes/user-themes';
import {Button} from '@ui/buttons/button';

interface UserThemeSelectorProps {
  themes: UserThemeInfo[];
  selectedThemeId: number | null;
  isLoading?: boolean;
  isApplying?: boolean;
  onSelect: (themeId: number | null) => void;
}

export function UserThemeSelector({
  themes,
  selectedThemeId,
  isLoading,
  isApplying,
  onSelect,
}: UserThemeSelectorProps) {
  if (isLoading) {
    return <UserThemeSelectorSkeleton />;
  }

  if (!themes.length) {
    return (
      <div className="rounded-lg border border-dashed p-18 text-sm text-muted">
        <Trans message="No themes are currently available for your account." />
      </div>
    );
  }

  return (
    <div className="space-y-20">
      {selectedThemeId === null ? (
        <div className="rounded-lg border bg-alt/50 px-14 py-12 text-sm text-muted">
          <Trans message="You are currently using the site default appearance." />
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-16 md:grid-cols-2 xl:grid-cols-3">
        {themes.map(theme => {
          const isSelected = `${selectedThemeId}` === `${theme.id}`;
          const palette = getThemePreviewPalette(theme);

          return (
            <div
              key={theme.id}
              className="overflow-hidden rounded-lg border"
              style={{
                backgroundColor: palette.paper,
                borderColor: palette.border,
              }}
            >
              <div
                className="border-b p-16"
                style={{
                  backgroundColor: palette.previewBackground,
                  borderColor: palette.border,
                }}
              >
                <div className="flex items-center gap-10">
                  <span
                    className="block h-16 w-16 rounded-full border border-white/20"
                    style={{backgroundColor: palette.primary}}
                  />
                  <span
                    className="block h-16 w-40 rounded-full border border-white/10"
                    style={{backgroundColor: palette.paper}}
                  />
                  <span
                    className="block h-16 flex-1 rounded-full border border-white/10"
                    style={{backgroundColor: palette.primarySoft}}
                  />
                </div>
              </div>

              <div className="space-y-14 p-16">
                <div className="flex items-start justify-between gap-12">
                  <div className="min-w-0">
                    <div
                      className="truncate text-sm font-semibold"
                      style={{color: palette.textMain}}
                    >
                      {theme.name}
                    </div>
                    <div className="mt-4 text-xs" style={{color: palette.textMuted}}>
                      {theme.is_dark ? (
                        <Trans message="Dark theme" />
                      ) : (
                        <Trans message="Light theme" />
                      )}
                    </div>
                  </div>

                  {isSelected ? (
                    <span
                      className="rounded-full px-8 py-4 text-xs font-medium"
                      style={{
                        backgroundColor: palette.primaryBadgeBg,
                        color: palette.primary,
                      }}
                    >
                      <Trans message="Active" />
                    </span>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="inline-flex h-36 min-w-[114px] items-center justify-center rounded-full border px-16 text-sm font-semibold transition-opacity disabled:opacity-60"
                  disabled={isApplying}
                  style={
                    isSelected
                      ? {
                          backgroundColor: 'transparent',
                          borderColor: palette.primary,
                          color: palette.primary,
                        }
                      : {
                          backgroundColor: palette.primary,
                          borderColor: palette.primary,
                          color: palette.onPrimary,
                        }
                  }
                  onClick={() => onSelect(Number(theme.id))}
                >
                  {isSelected ? (
                    <Trans message="Selected" />
                  ) : (
                    <Trans message="Select" />
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-start">
        <Button
          size="sm"
          variant={selectedThemeId === null ? 'flat' : 'outline'}
          color="primary"
          disabled={isApplying}
          onClick={() => onSelect(null)}
        >
          <Trans message="Use site default" />
        </Button>
      </div>
    </div>
  );
}

function getThemePreviewPalette(theme: UserThemeInfo) {
  const values = theme.values ?? {};
  const fallbackForeground = theme.is_dark ? '255 255 255' : '0 0 0';
  const fallbackBackground = theme.is_dark ? '30 30 38' : '255 255 255';
  const fallbackPaper = theme.is_dark ? '35 35 44' : '255 255 255';
  const fallbackPrimary = '104 159 56';
  const fallbackOnPrimary = '255 255 255';

  return {
    previewBackground: themeValueToCssColor(
      values['--be-background'],
      fallbackBackground,
    ),
    paper: themeValueToCssColor(values['--be-paper'], fallbackPaper),
    primary: themeValueToCssColor(values['--be-primary'], fallbackPrimary),
    onPrimary: themeValueToCssColor(
      values['--be-on-primary'],
      fallbackOnPrimary,
    ),
    primarySoft: themeValueToCssColorWithOpacity(
      values['--be-primary'],
      '0.45',
      fallbackPrimary,
    ),
    primaryBadgeBg: themeValueToCssColorWithOpacity(
      values['--be-primary'],
      '0.12',
      fallbackPrimary,
    ),
    border: themeValueToCssColorWithOpacity(
      values['--be-foreground-base'],
      normalizeOpacity(values['--be-divider-opacity'], '12%'),
      fallbackForeground,
    ),
    textMain: themeValueToCssColorWithOpacity(
      values['--be-foreground-base'],
      normalizeOpacity(values['--be-text-main-opacity'], theme.is_dark ? '100%' : '87%'),
      fallbackForeground,
    ),
    textMuted: themeValueToCssColorWithOpacity(
      values['--be-foreground-base'],
      normalizeOpacity(values['--be-text-muted-opacity'], theme.is_dark ? '70%' : '60%'),
      fallbackForeground,
    ),
  };
}

function normalizeOpacity(value: string | undefined, fallback: string) {
  const normalized = value || fallback;

  if (normalized.endsWith('%')) {
    const percentValue = Number(normalized.replace('%', ''));
    if (!Number.isNaN(percentValue)) {
      return `${percentValue / 100}`;
    }
  }

  return normalized;
}

function themeValueToCssColor(value: string | undefined, fallback: string) {
  const normalized = value || fallback;
  return normalized.startsWith('#') ||
    normalized.startsWith('rgb') ||
    normalized.startsWith('hsl')
    ? normalized
    : `rgb(${normalized})`;
}

function themeValueToCssColorWithOpacity(
  value: string | undefined,
  opacity: string,
  fallback: string,
) {
  const normalized = value || fallback;
  return normalized.startsWith('#') ||
    normalized.startsWith('rgb') ||
    normalized.startsWith('hsl')
    ? normalized
    : `rgb(${normalized} / ${opacity})`;
}

function UserThemeSelectorSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-16 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({length: 3}).map((_, index) => (
        <div key={index} className="overflow-hidden rounded-lg border bg-paper">
          <div className="border-b p-16">
            <Skeleton className="h-16 w-full" display="block" />
          </div>
          <div className="space-y-12 p-16">
            <Skeleton className="h-16 w-3/4" display="block" />
            <Skeleton className="h-12 w-1/3" display="block" />
            <Skeleton className="h-36 w-full" display="block" />
          </div>
        </div>
      ))}
    </div>
  );
}
